import { useNavigate } from "@tanstack/react-router";
import { ChevronsUpDown, Home, LifeBuoy, LogOut, Moon, Sun, User } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { TierBadge } from "@/components/shared/TierBadge";
import { useAuthStore } from "@/store/authStore";
import { useThemeStore } from "@/store/themeStore";
import { getFileUrl } from "@/lib/api-client";
import { cn } from "@/lib/utils";
import { initials, rolePaths } from "./nav";

export function UserAvatar({ className }: { className?: string }) {
  const user = useAuthStore((s) => s.user);
  return (
    <Avatar className={cn("size-9 rounded-full", className)}>
      <AvatarImage src={getFileUrl(user?.avatar)} alt="" />
      <AvatarFallback className="rounded-full bg-pastel-lavender text-pastel-lavender-ink">
        {initials(user?.name)}
      </AvatarFallback>
    </Avatar>
  );
}

export default function UserMenu({
  variant = "topbar",
  collapsed = false,
}: {
  variant?: "topbar" | "sidebar";
  collapsed?: boolean;
}) {
  const { user, logout } = useAuthStore();
  const mode = useThemeStore((s) => s.mode);
  const toggleTheme = useThemeStore((s) => s.toggleTheme);
  const navigate = useNavigate();
  const paths = rolePaths(user?.role);

  const handleLogout = () => {
    logout();
    navigate({ to: "/auth/login" });
  };

  const handleHelp = () => {
    if (user?.role === "admin") navigate({ to: "/admin/help-support" as any });
    else if (user?.role === "partner") navigate({ to: "/partner/support" as any });
    else navigate({ to: "/student/support" as any });
  };

  const trigger =
    variant === "sidebar" && !collapsed ? (
      <button
        type="button"
        className="flex w-full min-w-0 items-center gap-3 rounded-2xl p-2 text-left transition-colors hover:bg-sidebar-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring data-[state=open]:bg-sidebar-accent"
      >
        <UserAvatar className="size-10" />
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-1">
            <span className="truncate text-sm font-medium text-foreground">
              {user?.name || "User"}
            </span>
            {user?.role === "student" && (
              <TierBadge
                tier={(user?.membershipTier || "free") as any}
                verificationStatus={user?.verificationStatus}
                minimal
                showTooltip={false}
              />
            )}
          </span>
          <span className="block truncate text-caption text-muted-foreground capitalize">
            {user?.role || "member"}
          </span>
        </span>
        <ChevronsUpDown className="size-3.5 shrink-0 text-mute" aria-hidden />
      </button>
    ) : (
      <button
        type="button"
        aria-label="Account menu"
        className={cn(
          "flex items-center rounded-full transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          variant === "sidebar" && "mx-auto",
        )}
      >
        <UserAvatar />
      </button>
    );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent
        align={variant === "sidebar" ? "start" : "end"}
        side={variant === "sidebar" ? "top" : "bottom"}
        className="w-60"
      >
        <div className="flex items-center gap-2.5 px-2 py-2">
          <UserAvatar />
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-foreground">{user?.name || "User"}</p>
            <p className="truncate text-caption text-muted-foreground">{user?.email}</p>
          </div>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuItem onSelect={() => navigate({ to: paths.profile as any })}>
            <User />
            {user?.role === "admin" ? "Settings" : "My profile"}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => navigate({ to: "/" })}>
            <Home />
            Home page
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={(e) => {
              e.preventDefault();
              toggleTheme();
            }}
          >
            {mode === "dark" ? <Sun /> : <Moon />}
            {mode === "dark" ? "Light theme" : "Dark theme"}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={handleHelp}>
            <LifeBuoy />
            Help & support
          </DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={handleLogout} className="text-destructive focus:text-destructive">
          <LogOut />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
